var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pdfPageElementClassName } from "./parts/Page";
import { getClassNameForSubstitutionType } from "./PDFSubstitution";
import { PDFHelper } from "./PDFHelper";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var WindowState = /*#__PURE__*/ function (WindowState) { WindowState[WindowState["READY"] = 0] = "READY"; WindowState[WindowState["RENDERING"] = 1] = "RENDERING"; WindowState[WindowState["RENDER_COMPLETE"] = 2] = "RENDER_COMPLETE"; return WindowState; }(WindowState || {});
export const PDFGenContext = /*#__PURE__*/ createContext({ print: () => Promise.reject("not setup yet"), isPrintingRef: { current: false }, isPrinting: false });
const debug = false;
export const pdfWrapper = debug ? {} : {
    // require clientBoundingRect() to give non-zero values
    visibility: "hidden", height: 0
};
function getStateName(w) { switch (w) {
    case WindowState.READY: return "ready";
    case WindowState.RENDERING: return "rendering";
    case WindowState.RENDER_COMPLETE: return "render-complete";
} }
export function PDFGenProvider(props) {
    const [windowState, setWindowState] = useState(WindowState.READY);
    const [childToRender, setChildToRender] = useState();
    const [childRef, setChildRef] = useState();
    const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false);
    const [onComplete, setOnComplete] = useState();
    const isPrinting = windowState !== WindowState.READY;
    const isPrintingRef = useRef(false);
    isPrintingRef.current = isPrinting;
    console.log("PDFGen", getStateName(windowState));
    const renderComplete = useCallback(() => setWindowState(prev => { if (prev !== WindowState.RENDERING) {
        console.warn("invalid state", prev);
        return prev;
    } return WindowState.RENDER_COMPLETE; }), []);
    const print = useCallback((render, cfg) => {
        if (windowState !== WindowState.READY) {
            const err = new Error("invalid window-state " + windowState);
            console.error(err);
            return Promise.reject(err);
        } // set isPrintingRef immediately after function call to prevent callers
        // from calling print() again while we're still rendering
        isPrintingRef.current = true;
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () { setWindowState(WindowState.RENDERING); setOnComplete({ resolve: resolve, reject: reject }); try {
            const element = yield render(renderComplete, cfg);
            setChildToRender(element);
        }
        catch (e) {
            console.error(e);
            setWindowState(WindowState.READY);
            reject(e);
        } }));
    }, [renderComplete, windowState]);
    useEffect(() => {
        if (windowState !== WindowState.RENDER_COMPLETE)
            return;
        if (!childRef)
            return;
        if (!onComplete)
            return;
        if (!debug)
            setChildToRender(undefined);
        doPageSubstitutions(childRef);
        const styles = Array.from(document.getElementsByTagName("style")).map(p => { if (p.innerHTML && p.innerHTML.length > 0)
            return p.outerHTML; const sht = p.sheet; if (sht.cssRules && sht.cssRules.length > 0) {
            let content = Array.from(sht.cssRules).map(r => r.cssText).join("\n");
            return p.outerHTML.replace("</style>", content + "</style>");
        } return p.outerHTML; }); // eslint-disable-next-line no-restricted-globals
        const host = location.protocol + "//" + location.host; // links needed for font-refs
        const links = Array.from(document.getElementsByTagName("link")).map(p => { return p.outerHTML.replace(/(href=["'])\//g, "$1" + host + "/"); }); // fix page size so that page breaks work correctly
        styles.push("<style>body { height: auto; width: auto; position: relative; }</style>");
        const body = childRef.innerHTML;
        const content = [document.doctype ? new XMLSerializer().serializeToString(document.doctype) : "", // doctype matters for default user-agent stylesheets
            "<html>", `<meta charset="utf-8" />`, // to deal with promo characters
            ...links, ...styles, `<body style="-webkit-print-color-adjust:exact; margin: 0; padding: 0;">`, body, `</body></html>`].join("\n");
        try {
            onComplete.resolve(content);
        }
        catch (e) { }
        setWindowState(WindowState.READY);
        isPrintingRef.current = false;
    }, [windowState, childRef, onComplete]);
    const ctx = useMemo(() => { return { print: print, isPrinting: isPrinting, isPrintingRef: isPrintingRef }; }, [print, isPrinting]);
    return /*#__PURE__*/ _jsxs(React.Fragment, { children: [/*#__PURE__*/ _jsx(PDFHelper, { childToRender: childToRender, setChildRef: setChildRef, setShowPopupBlockedModal: setShowPopupBlockedModal, showPopupBlockedModal: showPopupBlockedModal }), /*#__PURE__*/ _jsx(PDFGenContext.Provider, { value: ctx, children: props.children })] });
}
function doPageSubstitutions(ref) { const elements = Array.from(ref.getElementsByClassName(pdfPageElementClassName)); const pageCount = elements.length; elements.map((e, index) => { let placeholders = Array.from(e.getElementsByClassName(getClassNameForSubstitutionType("page-no"))); placeholders.map(el => el.innerHTML = (index + 1).toString()); placeholders = Array.from(e.getElementsByClassName(getClassNameForSubstitutionType("page-count"))); placeholders.map(el => el.innerHTML = pageCount.toString()); return null; }); }
